import React from 'react';
import { TabProps } from './types';
import { getWordStyles, getDifficultyBadge, getPhonemeScoreColor } from './utils';
import WordPlayButton from './WordPlayButton';

const PhonemesTab: React.FC<TabProps> = ({
  analyzedWords,
  azureAnalysis,
  getWordPhonemesDisplay,
  phonemesLoading,
}) => {
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
        {azureAnalysis ? 'Phoneme-Level Analysis' : 'Complete Phoneme Analysis'}
        {!azureAnalysis && phonemesLoading && (
          <span className="ml-2 text-xs text-purple-500 dark:text-purple-400 animate-pulse normal-case">Loading enhanced phonemes...</span>
        )}
      </h4>
      <div className="space-y-4">
        {analyzedWords.map((item, index) => {
          const hasAzurePhonemes = item.azurePhonemes && item.azurePhonemes.length > 0;
          const fallbackPhonemes = getWordPhonemesDisplay(item.word);

          return (
            <div key={index} className={`p-4 rounded-lg border-2 ${getWordStyles(item.status)}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <WordPlayButton word={item.word} size="md" />
                  <span className="text-xl font-bold">{item.word}</span>
                  {!azureAnalysis && (
                    <span className={`px-2 py-0.5 text-xs rounded ${getDifficultyBadge(item.difficulty)}`}>
                      {item.difficulty}
                    </span>
                  )}
                </div>
                {(item.status === 'correct' || item.status === 'unclear') && (
                  <span className="text-lg font-semibold">
                    {Math.round(item.confidence * 100)}%
                  </span>
                )}
                {item.status === 'substitution' && item.transcribed && (
                  <span className="text-lg font-semibold text-red-600 dark:text-red-400">
                    → {item.transcribed}
                  </span>
                )}
                {item.status === 'omission' && (
                  <span className="text-lg font-semibold text-red-600 dark:text-red-400">(skipped)</span>
                )}
                {item.status === 'insertion' && (
                  <span className="text-lg font-semibold text-purple-600 dark:text-purple-400">(extra)</span>
                )}
              </div>

              {/* Azure Phonemes with scores */}
              {hasAzurePhonemes ? (
                <div className="flex flex-wrap gap-2">
                  {item.azurePhonemes!.map((p, i) => (
                    <div
                      key={i}
                      className={`group relative rounded px-3 py-2 border shadow-sm cursor-help ${getPhonemeScoreColor(p.accuracyScore)}`}
                    >
                      <div className="flex flex-col items-center">
                        <span className="font-mono font-bold text-lg">{p.phoneme}</span>
                        <span className="text-xs font-semibold">{Math.round(p.accuracyScore)}%</span>
                      </div>
                      {p.nBestPhonemes && p.nBestPhonemes.length > 0 && (
                        <div className="
                          absolute bottom-full left-1/2 -translate-x-1/2 mb-2
                          bg-gray-900 dark:bg-gray-700 text-white text-xs rounded py-2 px-3
                          opacity-0 group-hover:opacity-100 transition-opacity
                          pointer-events-none z-50 min-w-[120px] shadow-lg
                        ">
                          <div className="font-semibold mb-1">What we heard:</div>
                          {p.nBestPhonemes.slice(0, 3).map((alt, j) => (
                            <div key={j} className="flex justify-between gap-2">
                              <span className="font-mono">{alt.phoneme}</span>
                              <span>{Math.round(alt.score * 100)}%</span>
                            </div>
                          ))}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {fallbackPhonemes.map((p, i) => (
                    <div
                      key={i}
                      className="group relative bg-white dark:bg-gray-700 rounded px-3 py-2 border border-gray-200 dark:border-gray-600 shadow-sm cursor-help"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-purple-600 dark:text-purple-400">{p.phoneme}</span>
                        <span className="text-sm text-gray-400 dark:text-gray-500">{p.ipa}</span>
                      </div>
                      <div className="
                        absolute bottom-full left-0 mb-2
                        bg-gray-900 dark:bg-gray-700 text-white text-xs rounded py-2 px-3
                        opacity-0 group-hover:opacity-100 transition-opacity
                        pointer-events-none z-50 w-48 text-center shadow-lg
                      ">
                        {p.tip}
                        <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Syllables if available */}
              {item.azureSyllables && item.azureSyllables.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Syllables:</div>
                  <div className="flex flex-wrap gap-2">
                    {item.azureSyllables.map((s, i) => (
                      <div
                        key={i}
                        className={`px-2 py-1 rounded text-sm border ${getPhonemeScoreColor(s.accuracyScore)}`}
                      >
                        <span className="font-medium">{s.syllable}</span>
                        <span className="ml-1 text-xs">{Math.round(s.accuracyScore)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend for Azure phoneme colors */}
      {azureAnalysis && (
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Phoneme Score Legend:</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded"></span>
              <span className="text-gray-600 dark:text-gray-400">80-100% Excellent</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded"></span>
              <span className="text-gray-600 dark:text-gray-400">60-79% Needs practice</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded"></span>
              <span className="text-gray-600 dark:text-gray-400">&lt;60% Focus area</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhonemesTab;
